import React, { useState, useEffect, useRef, useMemo } from "react";
import * as api from "../../api/index";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiPhone, FiMapPin, FiInfo, FiSearch, FiImage, FiPlus, FiMinus, FiChevronRight, FiCheckCircle } from "react-icons/fi";
import imageCompression from "browser-image-compression";

const categories = [
  {
    name: "📱 Gadgets & Tech",
    sub: [
      {
        name: "📲 Smartphones & Tablets",
        items: ["iPhone", "Samsung", "Infinix", "Tecno", "Xiaomi", "Redmi", "iPad", "Phone Tablet Accessories"],
      },
      {
        name: "🔌 Accessories for Phones & Tablets",
        items: ["Power Bank", "Charger & Power Adapters", "Cases", "Phone Screens", "Stands & Holder", "Cables", "Bluetooth Headsets", "Selfie Sticks"],
      },
      {
        name: "⌚ Smart Watches",
        items: ["Apple Watches", "Samsung Watches", "Oraimo Watches", "Huawei Watches", "Itel Watches", "Fitbit Watches", "Amazfit Watches"],
      },
      {
        name: "💻 Tablets",
        items: ["Apple Tablets", "Samsung Tablets", "Atouch Tablets", "Amazon Tablets", "Xiaomi Tablets", "Lenovo Tablets", "Modlo Tablets", "Oraimo Tablets", "Huawei Tablets", "Itel Tablets", "Fitbit Tablets", "Amazfit Tablets"],
      },
      {
        name: "🎧 Headphones",
        items: ["Apple Headphones", "Sony Headphones", "JBL Headphones", "Anker Headphones", "Oraimo Headphones", "Samsung Headphones", "Logitech Headphones", "Jabra Headphones", "Plantronics Headphones"],
      },
    ],
  },
  {
    name: "💻 Electronics",
    sub: [
      {
        name: "🖥️ Laptop & Computers",
        items: ["Apple (MacBook)", "HP", "Dell", "Lenovo", "ASUS", "Acer", "MSI", "Razer", "Samsung (Laptop & Computers)", "Microsoft (Laptop & Computers)", "Others (Laptop & Computers)"],
      },
      {
        name: "📺 TV & DVD Equipment",
        items: ["Television", "TVs", "TV Boxes", "Projectors", "TV Receivers", "Media Players", "Decoders", "Blu-Ray Players"],
      },
      {
        name: "🎮 Video Game Consoles",
        items: ["Playstation , Ps , Xbox"],
      },
      {
        name: "🔊 Audio & Music Equipment",
        items: ["Speaker", "Microphones", "Sound Systems", "Home Theater Systems", "Music Mixer", "Walkie Talkies", "Amplifier"],
      },
      {
        name: "📸 Photo & Video Cameras",
        items: ["Digital Cameras", "Video Cameras", "Drones", "DSLR Cameras", "Action Cameras", "Film Cameras"],
      },
      {
        name: "📹 Security & Surveillance",
        items: ["CCTV Cameras", "IP Cameras", "Digital Video Recorders", "CCTV Kit", "Motion Detectors", "Spy Glasses"],
      },
      {
        name: "📡 Networking Products",
        items: ["Router", "Switch", "Modem"],
      },
      {
        name: "🖨️ Printers & Scanners",
        items: ["Printers & Scanners"],
      },
      {
        name: "🧭 Computer Monitors",
        items: ["Computer Monitors"],
      },
      {
        name: "🧩 Computer Accessories",
        items: ["Keyboards", "Adapters", "Mice", "Batteries", "Cables (Computer Accessories)", "Webcams", "Flash Drives"],
      },
      {
        name: "⚙️ Accessories & Supplies for Electronics",
        items: ["Photo & Video Accessories", "Printers & Scanner Accessories", "Networking Product Accessories", "Audio & Music Accessories", "Security & Surveillance Accessories", "Game Console Accessories"],
      },
      {
        name: "🎮 Video Games",
        items: ["Video Games"],
      },
    ],
  },
  {
    name: "🚗 Vehicles & Transport",
    sub: [
      {
        name: "🚘 Cars",
        items: ["Toyota", "Honda", "Lexus", "BMW", "Mercedes-Benz", "Hyundai", "Ford"],
      },
      {
        name: "🏍️ Motorcycles & Scooters",
        items: ["TVS", "Honda (Motorcycles & Scooters)", "Qlink", "Kymco", "Bajaj", "Yamaha (Motorcycles & Scooters)", "Daylong (Motorcycles & Scooters)"],
      },
      {
        name: "🔧 Spare Parts & Accessories",
        items: ["Engine Parts", "Tyres & Wheels", "Electrical Parts", "Interior Accessories", "Exterior Accessories", "Audio Parts"],
      },
      {
        name: "🚜 Heavy Machinery",
        items: ["Excavators", "Cranes", "Forklifts", "Bulldozers", "Tractors", "Concrete Mixers", "Wheel Loaders"],
      },
    ],
  },
  {
    name: "👗 Style & Beauty",
    sub: [
      {
        name: "🧥 Men’s Fashion",
        items: ["Clothing", "Men Shoes", "Watches", "Accessories"],
      },
      {
        name: "👠 Women’s Fashion",
        items: ["Dresses", "Women Shoes", "Bags", "Jewelry"],
      },
      {
        name: "💄 Cosmetics & Skincare",
        items: ["Makeup", "Skincare", "Haircare", "Others (Cosmetics & Skincare)"],
      },
      {
        name: "💇 Hair & Beauty Tools",
        items: ["Hair Tools", "Nail Tools & Care", "Skincare & Makeup Tools", "Saloon & Spa Equipment"],
      },
      {
        name: "🧳 Luggage & Travel Bags",
        items: ["Backpacks", "Travel Bags"],
      },
    ],
  },
  {
    name: "🏠 Home, Furnishing & Appliances",
    sub: [
      {
        name: "🛋️ Furniture",
        items: ["Sofas", "Beds Frame", "Beds", "Dining Sets", "Wardrobes", "Tables & Chairs"],
      },
      {
        name: "🖼️ Home Decor",
        items: ["Curtains & Drapes", "Wall Art", "Clocks", "Rugs & Carpets", "Mirrors", "Mattresses", "BedSheet & Duvet", "Pillows"],
      },
      {
        name: "🍳 Kitchen Appliances",
        items: ["Blenders", "Microwaves", "Cookers", "Refrigerators", "Electric Kettles", "Air Fryers"],
      },
      {
        name: "💡 Lighting",
        items: ["Bulbs", "Ceiling Lights", "Lamps", "Outdoor Lighting"],
      },
      {
        name: "📺 Home Electronics",
        items: ["Washing Machines", "Air Conditioners", "Vacuum Cleaners", "Irons", "Sewing Machines", "Water Heaters", "Fans"],
      },
    ],
  },
  {
    name: "🎯 Leisure & Entertainment",
    sub: [
      {
        name: "🏋️ Sports & Gym Gear",
        items: ["Treadmills", "Bicycles & Tricycles", "Exercise Bikes", "Dumbbells", "Tennis Tables", "Ab Exercise Machine"],
      },
      {
        name: "🎸 Musical Instruments",
        items: ["Guitars", "Keyboards (Musical Instruments)", "Drums", "Speaker (Musical Instruments)", "DJ Equipment", "Saxophones", "Music Stands"],
      },
    ],
  },
  {
    name: "🏭 Commercial Equipment & Tools",
    sub: [
      {
        name: "🧾 Retail & Store Equipment",
        items: ["Mannequins", "Commercial Scales", "Showcases", "POS Terminals", "Shelves", "Trolleys", "Barcode Scanners"],
      },
      {
        name: "🍽️ Restaurant & Catering Equipment",
        items: ["Ovens", "Refrigeration", "Mixers & Blenders", "Cookers (Restaurant & Catering Equipment)", "Shawarma Machines"],
      },
      {
        name: "🛡️ Safety & Security Equipment",
        items: ["Safetywear", "Barriers", "Safety Signs", "Fire Extinguisher", "Alarm Systems", "Safety Gloves & Sleeves", "Biometric Machines"],
      },
    ],
  },
  {
    name: "🏗️ Building Materials & Electricals",
    sub: [
      {
        name: "🏠 Building Materials & Supplies",
        items: ["Tiles", "Roofing Materials", "Flooring Materials", "Handrails", "Cement", "Ceiling", "Paint"],
      },
      {
        name: "⚡ Electrical & Wiring",
        items: ["Generators", "Soundproof Generators", "Electrical Cables", "Stabilizers", "Transformers", "Inverter Batteries", "Power Inverters"],
      },
      {
        name: "🚿 Water & Plumbing Systems",
        items: ["Water Closet", "Water Taps", "Water Pumps", "Wash Hand Basins", "Shower Mixers", "Bathtubs", "Shower Cubicles"],
      },
      {
        name: "🔋 Solar Energy & Power Systems",
        items: ["Solar Panels", "Solar Inverters", "Solar Lights", "Deep Cycle Batteries", "Charge Controllers", "Solar Generators", "Solar Accessories (Cables, Connectors, Mounts)", "Complete Solar Systems"],
      },
    ],
  },
];

const lagosLGAs = [
  "All Lagos", "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry", "Epe", "Eti-Osa", "Ibeju-Lekki",
  "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe", "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo", "Shomolu", "Surulere",
];

const CompleteProfile = ({ refreshAuth }) => {
  const location = useLocation();
  const email = location.state?.email || localStorage.getItem("userEmail");
  const navigate = useNavigate();

  const [form, setForm] = useState({
    phone: "",
    address: "",
    lga: "",
    bio: "",
    categories: [],
    subCategories: [],
    gallery: [],
  });

  const [expanded, setExpanded] = useState(null);
  const [subExpanded, setSubExpanded] = useState(null);

  const [categoryError, setCategoryError] = useState("");
  const [galleryError, setGalleryError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [autoExpand, setAutoExpand] = useState(false);

  const descRef = useRef(null);
  const addressRef = useRef(null);
  const phoneRef = useRef(null);
  const lgaRef = useRef(null);

  // Memoize selected text to prevent lag on Android while typing
  const selectedText = useMemo(() => form.categories.join(", ") || "None", [form.categories]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredCategories(categories);
      setAutoExpand(false);
      setExpanded(null);
      return;
    }

    const lower = term.toLowerCase();
    const newFiltered = [];

    categories.forEach((cat) => {
      const matchedSubs = [];
      cat.sub.forEach((sub) => {
        const subNameMatch = sub.name.toLowerCase().includes(lower);
        const matchedItems = sub.items.filter((item) => item.toLowerCase().includes(lower));

        if (subNameMatch) {
          matchedSubs.push({ ...sub });
        } else if (matchedItems.length > 0) {
          matchedSubs.push({ ...sub, items: matchedItems });
        }
      });

      if (matchedSubs.length > 0 || cat.name.toLowerCase().includes(lower)) {
        newFiltered.push({ ...cat, sub: matchedSubs });
      }
    });

    setFilteredCategories(newFiltered);
    setAutoExpand(true);
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleGalleryChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length + form.gallery.length > 20) {
      alert("You can upload up to 20 images only.");
      return;
    }

    setGalleryError("");
    setIsSaving(true);

    try {
      const options = {
        maxSizeMB: 0.4,
        maxWidthOrHeight: 1024, 
        useWebWorker: true,
      };

      const compressedImages = await Promise.all(
        selectedFiles.map((file) => imageCompression(file, options))
      );

      setForm((prev) => ({
        ...prev,
        gallery: [...prev.gallery, ...compressedImages].slice(0, 20),
      }));
    } catch (error) {
      setGalleryError("Failed to compress images. Try smaller ones.");
    } finally {
      setIsSaving(false);
    }
  };

  const removeGalleryImage = (index) => {
    setForm({
      ...form,
      gallery: form.gallery.filter((_, i) => i !== index),
    });
  };

  const handleCategorySelect = (item, parentSubName) => {
    setCategoryError("");
    const alreadySelected = form.categories.includes(item);

    if (alreadySelected) {
      const newCategories = form.categories.filter((c) => c !== item);
      const otherItemsInSub = [];
      categories.forEach((cat) => {
        cat.sub.forEach((sub) => {
          if (sub.name === parentSubName) {
            sub.items.forEach((i) => {
              if (newCategories.includes(i)) otherItemsInSub.push(i);
            });
          }
        });
      });

      const newSubCategories =
        otherItemsInSub.length === 0
          ? form.subCategories.filter((s) => s !== parentSubName)
          : form.subCategories;

      setForm({
        ...form,
        categories: newCategories,
        subCategories: newSubCategories,
      });
    } else if (form.categories.length < 50) {
      const newSubCategories = form.subCategories.includes(parentSubName)
        ? form.subCategories
        : [...form.subCategories, parentSubName];

      setForm({
        ...form,
        categories: [...form.categories, item],
        subCategories: newSubCategories,
      });
    } else {
      setCategoryError("You can only select up to 50 categories.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    setCategoryError("");
    setGalleryError("");
    setSubmitError("");

    if (!form.phone.trim()) {
      phoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setCategoryError("Please enter a phone number.");
      return;
    }
    // if (!form.address.trim()) {
    //   addressRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    //   setCategoryError("Please enter an address.");
    //   return;
    // }
    if (!form.bio.trim()) {
      descRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setCategoryError("Please enter a bio.");
      return;
    }
    if (!form.lga.trim()) {
      lgaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setCategoryError("Please select an LGA.");
      return;
    }
    if (form.categories.length === 0) {
      setCategoryError("Please select at least one item you sell.");
      return;
    }
    if (form.gallery.length < 5) {
      setGalleryError("Please upload at least 5 gallery images.");
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("phone", form.phone);
      formData.append("address", form.address);
      formData.append("lga", form.lga);
      formData.append("bio", form.bio);
      formData.append("fields", JSON.stringify(form.categories));
      formData.append("subCategory", JSON.stringify(form.subCategories));

      form.gallery.forEach((file) => {
        formData.append("shopImages", file);
      });

      await api.CompleteProfileseller(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      navigate("/confirm-email", { state: { email: email } });
    } catch (error) {
      setSubmitError(error.response?.data?.message || "Error saving profile");
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!email) navigate(-1);
  }, [email, navigate]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden pb-20">
      {/* Decorative Circles */}
      <div className="absolute top-[-5%] right-[-5%] w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-40"></div>

      {/* Header */}
      <div className="bg-white px-6 py-6 flex items-center shadow-sm border-b border-gray-100 relative z-20">
        <button
          onClick={() => navigate(-1)}
          disabled={isSaving}
          className={`p-2 -ml-2 hover:bg-gray-100 rounded-full transition text-gray-700 ${isSaving && "opacity-50"}`}
        >
          <IoMdArrowBack size={24} />
        </button>
        <div className="ml-4">
          <h1 className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Merchant Center</h1>
          <p className="text-lg font-black text-gray-900 tracking-tighter">Setup Seller Store</p>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-3xl mx-auto w-full relative z-10">
        <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-gray-200/50 border border-gray-100">
          <fieldset disabled={isSaving} className="space-y-8">
            
            {/* Store Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                   <FiInfo size={18} />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">Basic Information</h3>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Store Phone Number</label>
                  <div className="relative">
                    <FiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      ref={phoneRef}
                      type="tel"
                      inputMode="numeric"
                      name="phone"
                      placeholder="Phone Number"
                      value={form.phone}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold"
                      required
                    />
                  </div>
                </div>

                {/* <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Store Address</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      ref={addressRef}
                      type="text"
                      name="address"
                      placeholder="Street, Building, Landmark"
                      value={form.address}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold"
                      required
                    />
                  </div>
                </div> */}

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Location LGA</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                    <select
                      ref={lgaRef}
                      name="lga"
                      value={form.lga}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-10 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold appearance-none cursor-pointer relative"
                      required
                    >
                      <option value="">Select LGA</option>
                      {lagosLGAs.map((lga) => (
                        <option key={lga} value={lga}>{lga}</option>
                      ))}
                    </select>
                    <FiChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Store Bio / Description</label>
                  <textarea
                    name="bio"
                    ref={descRef}
                    placeholder="Tell customers about what you sell...."
                    value={form.bio}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-sm font-bold min-h-[120px]"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Inventory Section */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                   <FiSearch size={18} />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">Inventory Setup</h3>
              </div>

              <div className="relative mb-6">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="AI Powered Search (e.g. iPhone, Laptop)"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-900 text-white placeholder:text-gray-500 rounded-2xl focus:ring-4 focus:ring-orange-100 transition-all text-sm font-bold outline-none"
                />
              </div>

              <div className="space-y-4">
                {filteredCategories.map((cat, idx) => (
                  <div key={cat.name} className="border border-gray-100 rounded-3xl overflow-hidden bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={() => !autoExpand && setExpanded(expanded === idx ? null : idx)}
                      className="w-full flex justify-between items-center px-6 py-5 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-black text-gray-900 text-sm tracking-tight">{cat.name}</span>
                      <div className={`p-1 rounded-full ${autoExpand || expanded === idx ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-400"}`}>
                        {autoExpand || expanded === idx ? <FiMinus size={14} /> : <FiPlus size={14} />}
                      </div>
                    </button>

                    {(autoExpand || expanded === idx) && (
                      <div className="px-6 pb-6 pt-2 space-y-6 animate-in slide-in-from-top-2 duration-300">
                        {cat.sub.map((sub) => (
                          <div key={sub.name} className="space-y-3">
                            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">{sub.name}</span>
                            <div className="flex flex-wrap gap-2">
                              {sub?.items?.map((item) => (
                                <button
                                  type="button"
                                  key={item}
                                  onClick={() => handleCategorySelect(item, sub.name)}
                                  className={`px-4 py-2.5 rounded-xl border text-[11px] font-black uppercase tracking-tight transition-all active:scale-95 ${
                                    form.categories.includes(item)
                                      ? "bg-orange-600 text-white border-orange-600 shadow-lg shadow-orange-100"
                                      : "bg-white text-gray-700 border-gray-100 hover:border-orange-200"
                                  }`}
                                >
                                  {item}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 bg-orange-50 rounded-2xl border border-orange-100">
                <p className="text-[10px] text-orange-800 font-black uppercase tracking-widest leading-relaxed">
                  <span className="opacity-50">Selected Assets:</span><br/> {selectedText}
                </p>
              </div>
            </div>

            {/* Gallery Section */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                   <FiImage size={18} />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">Store Showcase</h3>
              </div>

              <div className="relative group cursor-pointer">
                <div className="border-2 border-dashed border-gray-200 rounded-[2rem] p-10 text-center bg-gray-50 group-hover:bg-white group-hover:border-orange-400 transition-all duration-300">
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 text-gray-400 group-hover:text-orange-500 group-hover:scale-110 transition-all">
                    <FiImage size={24} />
                  </div>
                  <p className="text-xs font-black text-gray-900 uppercase tracking-widest">Select Store Images</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Up to 20 High-Res Photos (Min 5)</p>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              {galleryError && <p className="text-red-500 text-[10px] font-black uppercase mt-4 text-center">{galleryError}</p>}

              <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-8">
                {form.gallery.map((file, index) => (
                  <div key={index} className="relative aspect-square rounded-2xl overflow-hidden shadow-sm border border-gray-100 group animate-in zoom-in-50 duration-300">
                    <img src={URL.createObjectURL(file)} alt="gallery" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-2 right-2 bg-black/50 backdrop-blur-md text-white w-6 h-6 rounded-lg flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >✕</button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between items-center">
                 <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Capacity Tracker</span>
                 <span className="text-xs font-black text-gray-900">{form.gallery.length} / 20</span>
              </div>
              <div className="w-full h-1 bg-gray-100 rounded-full mt-2">
                 <div className="h-full bg-orange-600 rounded-full transition-all" style={{ width: `${(form.gallery.length / 20) * 100}%` }}></div>
              </div>
            </div>
          </fieldset>

          {categoryError && <p className="text-red-500 text-xs font-black uppercase mt-6 text-center">{categoryError}</p>}
          {submitError && <p className="text-red-500 text-xs font-black uppercase mt-2 text-center">{submitError}</p>}

          <div className="mt-10 border-t border-gray-100 pt-10">
            <button
              type="submit"
              disabled={isSaving}
              className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-4 shadow-xl transition-all active:scale-95 ${
                isSaving ? "bg-orange-100 text-orange-400 cursor-not-allowed shadow-none" : "bg-gray-900 text-white hover:bg-black shadow-gray-200"
              }`}
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  Uploading 
                </>
              ) : (
                <>
                  Complete Registration
                  <FiCheckCircle size={18} />
                </>
              )}
            </button>

            {isSaving && (
              <div className="mt-6 text-center space-y-3">
                <p className="text-[10px] font-black text-gray-400 uppercase animate-pulse">
                  ⏳ Processing images. Usually takes 1 min tops.
                </p>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;